import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from "./contexts/AuthContext";
import { FeatureFlagsProvider } from "./contexts/FeatureFlagsContext";
import AuthVisibilityBootstrap from "./components/AuthVisibilityBootstrap";
import PushSubscriptionSync from "./components/shared/PushSubscriptionSync";
import {Environment} from "./layouts/Environment";
import {PublicLayout} from "./layouts/PublicLayout";
import {TradeStationConnected} from "./pages/TradeStationConnected";
import Pricing from "./pages/Pricing";
import Features from "./pages/Features";
import Brokers from "./pages/Brokers";
import FlashSale from "./pages/FlashSale";
import Terms from "./pages/Terms";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Privacy from "./pages/Privacy";
import RiskDisclosure from "./pages/RiskDisclosure";
import Legal from "./pages/Legal";
import ContactUs from "./pages/ContactUs";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import Learn from "./pages/Learn";
import LearnSection from "./pages/LearnSection";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import BlogEdit from "./pages/BlogEdit";
import BlogGenerating from "./pages/BlogGenerating";
import Home from "./pages/Home";
import RequireAuth from "./components/RequireAuth";
import RequireAuthAndEarlyAccess from "./components/RequireAuthAndEarlyAccess";
import RequireSuperuser from "./components/RequireSuperuser";
import HomeRedirect from "./components/HomeRedirect";
import LandingToHomeRedirect from "./components/LandingToHomeRedirect";
import AnalyticsProvider from "./components/Analytics/AnalyticsProvider";
import { loadTickerData } from "./utils/tickerSearch";
import CookieConsentBanner from "./components/shared/CookieConsentBanner";
import GoogleAuthCallback from "./components/GoogleAuthCallback";
import NotFound from "./pages/NotFound";
import TraderQuiz from "./pages/TraderQuiz";
import TraderQuizProfiles from "./pages/TraderQuizProfiles";
import TraderQuizProfileDetail from "./pages/TraderQuizProfileDetail";
import TraderQuizSavedResult from "./pages/TraderQuizSavedResult";
import SharedAIChatPage from "./pages/SharedAIChatPage";
import { dashboardResearchPath, DASHBOARD_ROUTES } from "./constants/dashboardRoutes";
import AuthenticatedMobileShell from "./layouts/AuthenticatedMobileShell";
import DashboardShell from "./layouts/DashboardShell";
// Bottom-nav tab pages: eager so first tab open does not Suspense-unmount mobile chrome.
// Regression source: 801a8a2 lazy-loaded these under a root Suspense with a full-viewport
// fallback, which hid BottomNavTray/header until each chunk resolved.
import AccountOverview from "./pages/AccountOverview";
import DashboardMarketPage from "./pages/dashboard/DashboardMarketPage";
import DashboardPerformancePage from "./pages/dashboard/DashboardPerformancePage";
import DashboardHealthPage from "./pages/dashboard/DashboardHealthPage";
import DashboardWatchlistsPage from "./pages/dashboard/DashboardWatchlistsPage";
import DashboardAIPage from "./pages/dashboard/DashboardAIPage";
import DashboardTradePage from "./pages/dashboard/DashboardTradePage";
import { TradePage } from "./routes/tradePageLoad";
import { DashboardSettingsPage } from "./routes/settingsPageLoad";

function LegacyPathRedirect({ to }) {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: to, search: location.search, hash: location.hash }}
      replace
    />
  );
}

const LoginPage = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const RegisterPage = lazy(() => import('./pages/Register').then((m) => ({ default: m.Register })));
const TradeStationConnectedPage = TradeStationConnected;
const NotificationsPage = lazy(() => import('./pages/Notifications').then((m) => ({ default: m.Notifications })));
const OnboardingPage = lazy(() => import('./pages/Onboarding'));
const AdminAreaPage = lazy(() => import('./pages/AdminArea'));
const BugReportDetailPage = lazy(() => import('./pages/BugReportDetail'));
const DashboardJournalsPage = lazy(() => import('./pages/dashboard/DashboardJournalsPage'));
const DashboardAlertsPage = lazy(() => import('./pages/dashboard/DashboardAlertsPage'));
const DashboardAIAutomationsPage = lazy(() => import('./pages/dashboard/DashboardAIAutomationsPage'));
const DashboardTradingRulesPage = lazy(() => import('./pages/dashboard/DashboardTradingRulesPage'));
const DashboardResearchPage = lazy(() => import('./pages/dashboard/DashboardResearchPage'));
const DashboardBacktestPage = lazy(() => import('./pages/dashboard/DashboardBacktestPage'));
const DashboardRsStudyPage = lazy(() => import('./pages/dashboard/DashboardRsStudyPage'));
const ContentCreatorAreaPage = lazy(() => import('./pages/contentCreator/ContentCreatorArea'));
const ContentCreatorCarouselListPage = lazy(() => import('./pages/contentCreator/ContentCreatorCarouselList'));
const ContentCreatorCarouselEditorPage = lazy(() => import('./pages/contentCreator/ContentCreatorCarouselEditor'));
const ContentCreatorVideoEditorPage = lazy(() => import('./pages/contentCreator/ContentCreatorVideoEditor'));
const ContentCreatorPublishPage = lazy(() => import('./pages/contentCreator/ContentCreatorPublish'));
const ContentCreatorVideoCaptureWindowPage = lazy(() => import('./pages/contentCreator/ContentCreatorVideoCaptureWindow'));
const ContentCreatorQuizAdExportPage = lazy(() => import('./pages/contentCreator/ContentCreatorQuizAdExport'));
const AiVideoListPage = lazy(() => import('./pages/contentCreator/AiVideoList'));
const AiVideoDetailPage = lazy(() => import('./pages/contentCreator/AiVideoDetail'));
const ContentCreatorCatalogPage = lazy(() => import('./pages/contentCreator/ContentCreatorCatalog'));
const CreateHomePage = lazy(() => import('./pages/contentCreator/CreateHome'));
const ScriptStudioPage = lazy(() => import('./pages/contentCreator/ScriptStudio'));
const ScriptEditorPage = lazy(() => import('./pages/contentCreator/ScriptEditor'));
const TeleprompterWindowPage = lazy(() => import('./pages/contentCreator/TeleprompterWindow'));
const VideoEditorPage = lazy(() => import('./pages/contentCreator/VideoEditorPage'));
const PricingPage = Pricing;

/** Layout for /dashboard/* — sidebar shell + nested sub-pages (all tiers). */
const DashboardLayout = () => <DashboardShell />;

/** Legacy /company/:symbol URLs → dashboard research. */
function CompanyToResearchRedirect() {
  const { symbol } = useParams();
  return <Navigate to={dashboardResearchPath(symbol)} replace />;
}

const routeSuspenseFallback = (
  <div style={{ flex: 1, minHeight: 0, background: '#0b1118' }} aria-busy="true" />
);

const App = () => {
  // Load ticker data on app initialization
  useEffect(() => {
    loadTickerData();
  }, []);
  return (
    <FeatureFlagsProvider>
    <AuthProvider>
      <AuthVisibilityBootstrap />
      <PushSubscriptionSync />
      <Router>
      <AnalyticsProvider>
        {/* Suspense is scoped inside PublicLayout / Environment / AuthenticatedMobileShell.
            A root Suspense with a full-viewport fallback remounts mobile chrome on every
            cold lazy route (see AuthenticatedMobileShell). */}
        <Routes>
          {/* Public/Marketing pages - lightweight layout without trading infrastructure */}
          <Route element={<PublicLayout/>}>
            <Route path="/" element={<HomeRedirect/>} />
            <Route path="/home" element={<Home/>} />
            <Route path="/landing" element={<LandingToHomeRedirect/>} />
            <Route path="/pricing" element={<PricingPage/>} />
            <Route path="/features" element={<Features/>} />
            <Route path="/brokers" element={<Brokers/>} />
            <Route path="/flash-sale" element={<FlashSale/>} />
            <Route path="/login" element={<Suspense fallback={routeSuspenseFallback}><LoginPage/></Suspense>} />
            <Route path="/register" element={<Suspense fallback={routeSuspenseFallback}><RegisterPage/></Suspense>} />
            <Route path="/auth/google/success" element={<GoogleAuthCallback/>} />
            <Route path="/forgot-password" element={<ForgotPassword/>} />
            <Route path="/reset-password" element={<ResetPassword/>} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/risk-disclosure" element={<RiskDisclosure />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/learn/:section" element={<LearnSection />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/generating/:jobId" element={
              <RequireSuperuser>
                <BlogGenerating />
              </RequireSuperuser>
            } />
            <Route path="/blog/edit/:id" element={
              <RequireSuperuser>
                <BlogEdit />
              </RequireSuperuser>
            } />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/quiz/r/:token" element={<TraderQuizSavedResult />} />
            <Route path="/share/ai/:token" element={<SharedAIChatPage />} />
            <Route path="/share/ai" element={<SharedAIChatPage />} />
            <Route path="/quiz/profiles/:archetypeKey" element={<TraderQuizProfileDetail />} />
            <Route path="/quiz/profiles" element={<TraderQuizProfiles />} />
            <Route path="/quiz" element={<TraderQuiz />} />
          </Route>

          {/* Trading app pages - full Environment with Pusher, WebSockets, streams */}
          <Route element={<Environment/>}>
            <Route path="/onboarding" element={
              <RequireAuth>
                <Suspense fallback={routeSuspenseFallback}><OnboardingPage /></Suspense>
              </RequireAuth>
            } />
            {/* Shared mobile chrome for /trade + /dashboard — BottomNav stays mounted across tabs */}
            <Route
              element={
                <RequireAuthAndEarlyAccess>
                  <AuthenticatedMobileShell />
                </RequireAuthAndEarlyAccess>
              }
            >
              <Route path="/trade" element={<TradePage />} />
              <Route
                path="/trade/health"
                element={<Navigate to={DASHBOARD_ROUTES.health} replace />}
              />
              {/* Dashboard — nested routes inside DashboardShell (Pro + Essentials) */}
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<AccountOverview />} />
                <Route path="trade" element={<DashboardTradePage />} />
                <Route path="market" element={<DashboardMarketPage />} />
                <Route path="market/overview" element={<DashboardMarketPage />} />
                <Route path="market/relative-strength" element={<DashboardMarketPage />} />
                <Route path="market/positions" element={<DashboardMarketPage />} />
                <Route path="market/research" element={<DashboardMarketPage />} />
                <Route path="performance" element={<DashboardPerformancePage />} />
                <Route path="health" element={<DashboardHealthPage />} />
                <Route path="alerts" element={<Suspense fallback={routeSuspenseFallback}><DashboardAlertsPage /></Suspense>} />
                <Route path="watchlists" element={<DashboardWatchlistsPage />} />
                <Route path="journals" element={<Suspense fallback={routeSuspenseFallback}><DashboardJournalsPage /></Suspense>} />
                <Route path="research" element={<Suspense fallback={routeSuspenseFallback}><DashboardResearchPage /></Suspense>} />
                <Route path="ai" element={<DashboardAIPage />} />
                <Route path="ai-automations/new" element={<Suspense fallback={routeSuspenseFallback}><DashboardAIAutomationsPage /></Suspense>} />
                <Route path="ai-automations/:automationId" element={<Suspense fallback={routeSuspenseFallback}><DashboardAIAutomationsPage /></Suspense>} />
                <Route path="ai-automations" element={<Suspense fallback={routeSuspenseFallback}><DashboardAIAutomationsPage /></Suspense>} />
                <Route path="trading-rules" element={<Suspense fallback={routeSuspenseFallback}><DashboardTradingRulesPage /></Suspense>} />
                <Route path="journal-template" element={<Navigate to="/dashboard/settings?section=journalTemplate" replace />} />
                <Route path="settings" element={<Suspense fallback={routeSuspenseFallback}><DashboardSettingsPage /></Suspense>} />
                <Route path="backtest" element={
                  <RequireSuperuser>
                    <Suspense fallback={routeSuspenseFallback}><DashboardBacktestPage /></Suspense>
                  </RequireSuperuser>
                } />
                <Route path="rs-study" element={
                  <RequireSuperuser>
                    <Suspense fallback={routeSuspenseFallback}><DashboardRsStudyPage /></Suspense>
                  </RequireSuperuser>
                } />
              </Route>
              <Route
                path="/performance"
                element={<Navigate to="/dashboard/performance" replace />}
              />
            </Route>
            <Route path="/settings" element={<LegacyPathRedirect to="/dashboard/settings" />} />
            <Route path="/rule-configuration" element={<LegacyPathRedirect to="/dashboard/trading-rules" />} />
            <Route path="/admin/:section?" element={
              <RequireSuperuser>
                <Suspense fallback={routeSuspenseFallback}><AdminAreaPage /></Suspense>
              </RequireSuperuser>
            } />
            <Route path="/admin/bug-reports/:id" element={
              <RequireSuperuser>
                <Suspense fallback={routeSuspenseFallback}><BugReportDetailPage /></Suspense>
              </RequireSuperuser>
            } />
            <Route path="/content-creator" element={
              <RequireSuperuser>
                <Suspense fallback={routeSuspenseFallback}><ContentCreatorAreaPage /></Suspense>
              </RequireSuperuser>
            }>
              <Route index element={<Suspense fallback={routeSuspenseFallback}><CreateHomePage /></Suspense>} />
              <Route path="projects" element={<Suspense fallback={routeSuspenseFallback}><CreateHomePage /></Suspense>} />
              <Route path="scripts" element={<Suspense fallback={routeSuspenseFallback}><ScriptStudioPage /></Suspense>} />
              <Route path="scripts/:id" element={<Suspense fallback={routeSuspenseFallback}><ScriptEditorPage /></Suspense>} />
              <Route path="carousels" element={<Suspense fallback={routeSuspenseFallback}><ContentCreatorCarouselListPage /></Suspense>} />
              <Route path="carousel/:id" element={<Suspense fallback={routeSuspenseFallback}><ContentCreatorCarouselEditorPage /></Suspense>} />
              <Route path="quiz-ad-export" element={<Suspense fallback={routeSuspenseFallback}><ContentCreatorQuizAdExportPage /></Suspense>} />
              <Route path="video" element={<Suspense fallback={routeSuspenseFallback}><ContentCreatorVideoEditorPage /></Suspense>} />
              <Route path="ai-videos" element={<Suspense fallback={routeSuspenseFallback}><AiVideoListPage /></Suspense>} />
              <Route path="ai-videos/:id" element={<Suspense fallback={routeSuspenseFallback}><AiVideoDetailPage /></Suspense>} />
              <Route path="catalog" element={<Suspense fallback={routeSuspenseFallback}><ContentCreatorCatalogPage /></Suspense>} />
              <Route path="publish" element={<Suspense fallback={routeSuspenseFallback}><ContentCreatorPublishPage /></Suspense>} />
            </Route>
            <Route path="/content-creator/edit/:projectId" element={
              <RequireSuperuser>
                <Suspense fallback={routeSuspenseFallback}><VideoEditorPage /></Suspense>
              </RequireSuperuser>
            } />
            <Route path="/content-creator/teleprompter" element={
              <RequireSuperuser>
                <Suspense fallback={routeSuspenseFallback}><TeleprompterWindowPage /></Suspense>
              </RequireSuperuser>
            } />
            <Route path="/content-creator/video/capture-window" element={
              <RequireSuperuser>
                <Suspense fallback={routeSuspenseFallback}><ContentCreatorVideoCaptureWindowPage /></Suspense>
              </RequireSuperuser>
            } />
            <Route path="/company/:symbol?" element={
              <RequireAuthAndEarlyAccess>
                <CompanyToResearchRedirect />
              </RequireAuthAndEarlyAccess>
            } />
            <Route path="/connected" element={
              <RequireAuthAndEarlyAccess>
                <TradeStationConnectedPage/>
              </RequireAuthAndEarlyAccess>
            }/>
            <Route path="/subscription-success" element={
              <RequireAuth>
                <SubscriptionSuccess />
              </RequireAuth>
            } />
            <Route path="/notify" element={<Suspense fallback={routeSuspenseFallback}><NotificationsPage/></Suspense>} />
          </Route>

          {/* Unknown paths: do not render HomePageRouter (that hid /landing.html-style URLs) */}
          <Route element={<PublicLayout/>}>
            <Route path="*" element={<NotFound/>} />
          </Route>
        </Routes>
        <CookieConsentBanner />
      </AnalyticsProvider>
      </Router>
    </AuthProvider>
    </FeatureFlagsProvider>
  );
};

export default App;
